import type { ReactNode } from 'react';
import { NJButton, NJCheckbox, NJText } from '@engie-group/fluid-design-system-react';
import { UMFormStyles, UMInput, UMR, UMSelect } from '../../urbanmap/_components';
import { candidates, labelOf, YEARS } from './engine';
import type { AnySet, DemandeSet, DevSet, ImportanceSet, PhasageSet, PointsDepartSet, TraceSet } from './types';

export function Field({ label, unit, children }: { label: string; unit?: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
      <NJText scale="sm" style={{ margin: 0 }}>{label}</NJText>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1 }}>{children}</div>
        {unit && <span style={{ fontSize: 12, color: UMR.textSecondary, minWidth: 56 }}>{unit}</span>}
      </div>
    </div>
  );
}

export const NumField = ({ label, unit, value, onChange }: { label: string; unit?: string; value: number; onChange: (n: number) => void }) => (
  <Field label={label} unit={unit}><UMInput type="number" value={String(value)} ariaLabel={label} onChange={(x) => onChange(Number(x) || 0)} /></Field>
);

export const YearField = ({ label, value, onChange }: { label: string; value: string; onChange: (y: string) => void }) => (
  <Field label={label}><UMSelect value={value} ariaLabel={label} options={YEARS.map((y) => ({ value: y, label: y }))} onChange={onChange} /></Field>
);

const Hint = ({ children }: { children: ReactNode }) => <div style={{ fontSize: 12, color: UMR.textSecondary, margin: '-4px 0 12px' }}>{children}</div>;

interface FormProps<T extends AnySet> { value: T; onChange: (s: T) => void }

/** Sélection de bâtiments (déclencheurs / obligatoires), aussi cliquable sur la carte. */
function BuildingPicker({ ids, color, onChange }: { ids: string[]; color: string; onChange: (ids: string[]) => void }) {
  return (
    <>
      <Hint>Cliquez un bâtiment sur la carte pour l’ajouter ou le retirer.</Hint>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }} aria-label="Bâtiments sélectionnés">
        {ids.length === 0 && <span style={{ fontSize: 14, color: UMR.textSecondary }}>Aucun bâtiment sélectionné.</span>}
        {ids.map((id) => (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, borderLeft: `4px solid ${color}`, paddingLeft: 8 }}>
            <span style={{ flex: 1 }}>{labelOf(id)}</span>
            <NJButton label="Retirer" variant="secondary" emphasis="minimal" scale="sm" onClick={() => onChange(ids.filter((x) => x !== id))} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <NJButton label="Top 3 consommateurs" variant="secondary" scale="sm" onClick={() => onChange(candidates(3).map((b) => b.id))} />
        <NJButton label="Vider" variant="secondary" emphasis="minimal" scale="sm" disabled={!ids.length} onClick={() => onChange([])} />
      </div>
    </>
  );
}

function DemandeForm({ value: s, onChange }: FormProps<DemandeSet>) {
  return (
    <>
      <NumField label="Coefficient sur la consommation retenue" unit="%" value={s.consoCoef} onChange={(consoCoef) => onChange({ ...s, consoCoef })} />
      <NumField label="Taux de raccordement" unit="%" value={s.connectionRate} onChange={(connectionRate) => onChange({ ...s, connectionRate: Math.min(100, Math.max(0, connectionRate)) })} />
      <NumField label="Érosion (coefficient de consommation par an)" unit="%/an" value={s.erosion} onChange={(erosion) => onChange({ ...s, erosion })} />
    </>
  );
}

function TraceForm({ value: s, onChange }: FormProps<TraceSet>) {
  const set = (patch: Partial<TraceSet>) => onChange({ ...s, ...patch });
  return (
    <>
      <NJText as="h4" scale="md" style={{ margin: '0 0 8px' }}>Pondération des routes (carte)</NJText>
      <NumField label="Routes interdites" unit="routes" value={s.forbiddenRoads} onChange={(forbiddenRoads) => set({ forbiddenRoads: Math.max(0, forbiddenRoads) })} />
      <NumField label="Routes obligatoires" unit="routes" value={s.mandatoryRoads} onChange={(mandatoryRoads) => set({ mandatoryRoads: Math.max(0, mandatoryRoads) })} />
      <NumField label="Routes pondérées (xN)" unit="routes" value={s.weightedRoads} onChange={(weightedRoads) => set({ weightedRoads: Math.max(0, weightedRoads) })} />
      <Hint>Mock : rouge = interdites, vert = obligatoires, jaune = pondérées (tronçons du réseau de la carte).</Hint>
      <NJText as="h4" scale="md" style={{ margin: '16px 0 8px' }}>Réseau existant</NJText>
      <div style={{ marginBottom: 12 }}><NJCheckbox label="Réutiliser le réseau existant" isChecked={s.useExistingNetwork} onChange={(_, c) => set({ useExistingNetwork: !!c })} /></div>
    </>
  );
}

function DevForm({ value: s, onChange }: FormProps<DevSet>) {
  const set = (patch: Partial<DevSet>) => onChange({ ...s, ...patch });
  return (
    <>
      <Field label="Objectif de développement prioritaire">
        <UMSelect value={s.objective} ariaLabel="Objectif de développement" onChange={(v) => set({ objective: v as DevSet['objective'] })}
          options={[{ value: 'km', label: 'Kilomètres de réseau' }, { value: 'mwh', label: 'MWh raccordés' }, { value: 'mixte', label: 'Mixte (km + MWh)' }]} />
      </Field>
      <NumField label="Objectif — réseau" unit="km" value={s.targetKm} onChange={(targetKm) => set({ targetKm })} />
      <NumField label="Objectif — énergie" unit="MWh" value={s.targetMWh} onChange={(targetMWh) => set({ targetMWh })} />
      <Field label="Autres objectifs">
        <UMInput value={s.otherObjective} ariaLabel="Autres objectifs" onChange={(otherObjective) => set({ otherObjective })} />
      </Field>
    </>
  );
}

function PhasageForm({ value: s, onChange }: FormProps<PhasageSet>) {
  const set = (patch: Partial<PhasageSet>) => onChange({ ...s, ...patch });
  return (
    <>
      <NumField label="Réseau construit par an" unit="km/an" value={s.kmPerYear} onChange={(kmPerYear) => set({ kmPerYear })} />
      <NumField label="Énergie raccordée par an" unit="MWh/an" value={s.mwhPerYear} onChange={(mwhPerYear) => set({ mwhPerYear })} />
      <NumField label="Clients raccordés par an" unit="clients/an" value={s.clientsPerYear} onChange={(clientsPerYear) => set({ clientsPerYear: Math.max(1, clientsPerYear) })} />
    </>
  );
}

function ImportanceForm({ value: s, onChange }: FormProps<ImportanceSet>) {
  const set = (patch: Partial<ImportanceSet>) => onChange({ ...s, ...patch });
  return (
    <>
      <Field label="Critère d’importance prioritaire">
        <UMSelect value={s.priority} ariaLabel="Critère d’importance" onChange={(v) => set({ priority: v as ImportanceSet['priority'] })}
          options={[{ value: 'obligatoire', label: 'Clients obligatoires' }, { value: 'densification', label: 'Densification' }, { value: 'mixte', label: 'Mixte' }]} />
      </Field>
      <Field label="Niveau de densification visé">
        <UMSelect value={s.densification} ariaLabel="Densification" onChange={(v) => set({ densification: v as ImportanceSet['densification'] })}
          options={[{ value: 'faible', label: 'Faible' }, { value: 'moyenne', label: 'Moyenne' }, { value: 'forte', label: 'Forte' }]} />
      </Field>
      <NJText as="h4" scale="md" style={{ margin: '16px 0 4px' }}>Bâtiments obligatoires</NJText>
      <BuildingPicker ids={s.mandatoryBuildings} color="#7a3fd1" onChange={(mandatoryBuildings) => set({ mandatoryBuildings })} />
      <section style={{ border: `1px solid ${UMR.border}`, padding: 12 }}>
        <NJText as="h4" scale="md">Bâtiments déclencheurs · V2</NJText>
        <NJText scale="sm">Hypothèse pour l’optimisation : associer un bâtiment déclencheur à une année de raccordement imposée.</NJText>
        <fieldset disabled style={{ border: 0, padding: 0, margin: 0 }}>
          <UMSelect value={candidates(1)[0]?.id ?? ''} ariaLabel="Bâtiment déclencheur V2"
            options={candidates(5).map((building) => ({ value: building.id, label: building.address }))} onChange={() => {}} />
          <YearField label="Année de raccordement · V2" value="2030" onChange={() => {}} />
        </fieldset>
        <NJText scale="xs">Exemple illustratif, non enregistré et non utilisé par le calcul V1. Optimisation désactivée.</NJText>
      </section>
    </>
  );
}

function PointsDepartForm({ value: s, onChange }: FormProps<PointsDepartSet>) {
  const set = (patch: Partial<PointsDepartSet>) => onChange({ ...s, ...patch });
  return (
    <>
      <NJText as="h4" scale="md" style={{ margin: '0 0 8px' }}>Points de départ du réseau (carte)</NJText>
      <NumField label="Nombre de points de départ" unit="points" value={s.startPoints} onChange={(startPoints) => set({ startPoints: Math.max(1, startPoints) })} />
      <Hint>Mock : points verts placés sur la carte (premiers points de livraison du périmètre).</Hint>
      <Field label="Chaufferies">
        <UMSelect value={s.boilerRooms} ariaLabel="Chaufferies" onChange={(v) => set({ boilerRooms: v as PointsDepartSet['boilerRooms'] })}
          options={[{ value: 'existantes', label: 'Existantes' }, { value: 'creees', label: 'Créées' }, { value: 'mixte', label: 'Existantes + créées' }]} />
      </Field>
      <NJText as="h4" scale="md" style={{ margin: '16px 0 8px' }}>Production principale</NJText>
      <YearField label="Date de mise en service" value={s.commissioningYear} onChange={(commissioningYear) => set({ commissioningYear })} />
      <NumField label="Capacité installée" unit="MW" value={s.capacityMW} onChange={(capacityMW) => set({ capacityMW })} />
      <NJText as="h4" scale="md" style={{ margin: '16px 0 8px' }}>Appoint / secours</NJText>
      <YearField label="Date de mise en service" value={s.backupYear} onChange={(backupYear) => set({ backupYear })} />
      <NumField label="Capacité d’appoint" unit="MW" value={s.backupCapacityMW} onChange={(backupCapacityMW) => set({ backupCapacityMW })} />
    </>
  );
}

/** Éditeur d'un jeu : nom + formulaire propre à sa famille d'hypothèses. */
export function SetForm({ value, onChange }: { value: AnySet; onChange: (s: AnySet) => void }) {
  return (
    <fieldset style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
      <UMFormStyles />
      <Field label="Nom du jeu d’hypothèses"><UMInput value={value.name} ariaLabel="Nom du jeu d’hypothèses" onChange={(name) => onChange({ ...value, name })} /></Field>
      {value.name.trim() === '' && <div style={{ fontSize: 12, color: 'var(--nj-core-color-orange-800, #8a3c14)', margin: '-8px 0 12px' }}>Un nom clair est obligatoire.</div>}
      {value.kind === 'demande' && <DemandeForm value={value} onChange={onChange} />}
      {value.kind === 'trace' && <TraceForm value={value} onChange={onChange} />}
      {value.kind === 'developpement' && <DevForm value={value} onChange={onChange} />}
      {value.kind === 'phasage' && <PhasageForm value={value} onChange={onChange} />}
      {value.kind === 'importance' && <ImportanceForm value={value} onChange={onChange} />}
      {value.kind === 'pointsDepart' && <PointsDepartForm value={value} onChange={onChange} />}
    </fieldset>
  );
}
